import React, { useContext } from 'react'

import { isFunction } from 'lodash-es'

import { type ChangeTeamStatusValue } from '@/api/changeTeamStatusValue.ts'
import { type ButtonProps } from '@/components/atoms/Button/Button.tsx'
import {
  ConfirmDialog,
  type ConfirmDialogLabels,
  type ConfirmDialogState,
} from '@/components/molecules/ConfirmDialog/ConfirmDialog.tsx'
import {
  SelectTeamStatus,
  type SelectTeamStatusOptions,
} from '@/components/molecules/SelectTeamStatus/SelectTeamStatus.tsx'
import {
  SelectTeamStatusContext,
  SelectTeamStatusProvider,
} from '@/components/molecules/SelectTeamStatus/SelectTeamStatusProvider.tsx'
import { type TeamCarData } from '@/components/molecules/Status'

export interface ConfirmTeamDialogLabels extends Omit<ConfirmDialogLabels, 'description'> {
  description?: string | ((teamNumber: string) => string)
}

export interface ConfirmTeamDialogOptions extends SelectTeamStatusOptions {}

export interface ConfirmTeamDialogState extends ConfirmDialogState {}

export interface ConfirmTeamDialogData {
  team: TeamCarData & { usingMobileApp: boolean }
}

export type TeamStatus = ChangeTeamStatusValue

export interface ConfirmTeamDialogAction {
  onConfirm: (teamStatus: TeamStatus) => void
}

export interface ConfirmTeamDialogProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    ConfirmTeamDialogData,
    ConfirmTeamDialogState,
    ConfirmTeamDialogAction {
  variant?: ButtonProps['variant']
  labels: ConfirmTeamDialogLabels
  options: ConfirmTeamDialogOptions
}

export const ConfirmDialogWithSelectTeamStatus = React.forwardRef<HTMLDivElement, ConfirmTeamDialogProps>(
  ({ labels, options, team, onConfirm, ...props }, ref) => {
    const { teamStatus } = useContext(SelectTeamStatusContext)
    const description = isFunction(labels.description) ? labels.description(team.carNumber) : labels.description

    return (
      <ConfirmDialog
        labels={{ ...labels, description }}
        onConfirmDisabled={!teamStatus}
        onConfirm={() => {
          onConfirm(teamStatus!)
        }}
        {...props}
        ref={ref}>
        <SelectTeamStatus
          team={team}
          teamStatusOptions={options.teamStatusOptions}
          teamAvailableInRegionOptions={options.teamAvailableInRegionOptions}
        />
      </ConfirmDialog>
    )
  }
)

export const ConfirmTeamDialog = React.forwardRef<HTMLDivElement, ConfirmTeamDialogProps>(({ team, ...props }, ref) => (
  <SelectTeamStatusProvider defaultValue={team.usingMobileApp ? 'gps' : 'region'}>
    <ConfirmDialogWithSelectTeamStatus team={team} {...props} ref={ref} />
  </SelectTeamStatusProvider>
))
